/**
 * API Adapter Configuration
 * Bridges the frontend components with the real backend APIs
 */

import { Configuration } from '../Apis/configuration';
import { EcommerceConfig } from '../types';
import { AXIOS_CONFIG } from '../Apis/sharedConfig';

let apiConfiguration: Configuration | null = null;
let currentConfig: EcommerceConfig | null = null;

/**
 * Initialize the API configuration with store settings
 */
export function initializeApiAdapter(config: EcommerceConfig): Configuration {
  currentConfig = config;

  // Update the singleton AXIOS_CONFIG
  AXIOS_CONFIG.basePath = config.apiBaseUrl;
  AXIOS_CONFIG.apiKey = config.storeId;
  AXIOS_CONFIG.accessToken = () => getAuthToken() || '';

  apiConfiguration = AXIOS_CONFIG;

  return apiConfiguration;
}

/**
 * Get the current API configuration
 */
export function getApiConfiguration(): Configuration {
  if (!apiConfiguration) {
    throw new Error('API adapter not initialized. Call initializeApiAdapter first.');
  }
  return apiConfiguration;
}

/**
 * Get the current ecommerce config
 */
export function getCurrentConfig(): EcommerceConfig {
  if (!currentConfig) {
    throw new Error('API adapter not initialized.');
  }
  return currentConfig;
}

// Token management with store-scoped keys
function getTokenKey(): string {
  const storeId = currentConfig?.storeId || 'default';
  return `ecommerce_access_token_${storeId}`;
}

export function setAuthToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(getTokenKey(), token);
    // Update the configuration with new token
    if (apiConfiguration) {
      apiConfiguration.accessToken = () => token;
    }
    // Also update the singleton directly
    AXIOS_CONFIG.accessToken = () => token;
  }
}

export function getAuthToken(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(getTokenKey());
  }
  return null;
}

export function clearAuthToken(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(getTokenKey());
    // Clear from configuration
    if (apiConfiguration) {
      apiConfiguration.accessToken = () => '';
    }
    // Also update the singleton directly
    AXIOS_CONFIG.accessToken = () => '';
  }
}

/**
 * Change user password
 * Custom implementation to fix the auto-generated API which incorrectly sends passwords as headers
 */
export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  if (!currentConfig) {
    throw new Error('API adapter not initialized.');
  }

  const token = getAuthToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const url = `${currentConfig.apiBaseUrl}/auth/change-password`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Store-Key': currentConfig.storeId,
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      oldPassword,
      newPassword,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      response: {
        data: errorData,
        status: response.status,
      },
    };
  }
}

